/**
 * Work-order required-fields policy (shared contract).
 *
 * Single source of truth for the requireable field keys + labels, the policy
 * shape returned by the backend, and the pure validation helper reused by the
 * web and mobile create-work-order forms. Keys MUST match the backend allowlist
 * in entities.WorkOrderRequirableFields.
 */
export const WORK_ORDER_REQUIRABLE_FIELDS = [
    { key: 'color', label: 'Color' },
    { key: 'stock_number', label: 'Stock #' },
    { key: 'mileage', label: 'Mileage' },
    { key: 'ro_number', label: 'RO #' },
    { key: 'po_number', label: 'PO #' },
    { key: 'hat_number', label: 'HAT #' },
    // Tags are a work-order association, not a vehicle field; the create forms
    // enforce this one via the tag picker (only when the location has tags),
    // separately from the vehicle-field checks.
    { key: 'tags', label: 'Tag' },
    // Only while the account has the optional Customers module on: the create
    // forms enforce it through their Customer step, and the server ignores it
    // while the module is off. Settings offers it only while the module is on
    // (requirableWorkOrderFields).
    { key: 'customer', label: 'Customer' },
];
/**
 * Keys that are not a value on the vehicle form: a tag (the tag picker) and a
 * customer (the Customer step). Each create form checks these on its own.
 */
export const WORK_ORDER_NON_FORM_REQUIRABLE_FIELDS = ['tags', 'customer'];
// Vehicle/work-order scalar fields validated against the vehicle form. Excludes
// 'tags' (handled separately via the tag picker) and 'customer' (the Customer step).
export const WORK_ORDER_REQUIRABLE_VEHICLE_FIELDS = WORK_ORDER_REQUIRABLE_FIELDS.filter((f) => !WORK_ORDER_NON_FORM_REQUIRABLE_FIELDS.includes(f.key)).map((f) => f.key);
/**
 * The fields an account can make required, as offered in settings: Customer
 * only while the account has the Customers module on.
 */
export function requirableWorkOrderFields(opts) {
    return WORK_ORDER_REQUIRABLE_FIELDS.filter((f) => f.key !== 'customer' || opts.customers);
}
/** Human-friendly label for a requireable field key. */
export function workOrderFieldLabel(key) {
    const f = WORK_ORDER_REQUIRABLE_FIELDS.find((x) => x.key === key);
    return f ? f.label : key;
}
/**
 * Returns the subset of requiredKeys whose value in `values` is missing/blank.
 * `values` is keyed by the field keys above; each value may be the raw form
 * value (string | number | null | undefined). Mirrors the backend rule
 * (whitespace-only counts as missing).
 */
export function missingRequiredWoFields(requiredKeys, values) {
    const missing = [];
    for (const key of requiredKeys) {
        // A customer is no form value: the Customer step enforces it (and only
        // while the module is on). Checked here only when the caller passes it.
        if (key === 'customer' && !('customer' in values))
            continue;
        const v = values[key];
        if (v === undefined || v === null) {
            missing.push(key);
            continue;
        }
        if (typeof v === 'string' && v.trim() === '') {
            missing.push(key);
            continue;
        }
        if (typeof v === 'number' && Number.isNaN(v)) {
            missing.push(key);
        }
    }
    return missing;
}
